<template>
  <v-container class="modern-bitacora-view pb-12">
    <!-- Premium Global Header (Master Pattern) -->
    <v-row class="mb-6 align-center px-4 pt-4">
      <div class="d-flex align-center">
        <v-avatar color="primary" size="44" class="mr-4 shadow-sm border-white-sm">
          <v-icon color="white" small>fas fa-list-check</v-icon>
        </v-avatar>
        <div>
          <div class="text-h4 font-weight-black primary--text d-flex align-center lh-1">
            Bitácora de Producción
          </div>
          <div class="caption grey--text font-weight-bold uppercase tracking-widest mt-1">Registro detallado de actividades y tiempos por proceso</div>
        </div>
      </div>
      <v-spacer></v-spacer>
      <div class="d-flex align-center">
        <export-production-bitacora :filter="filter" :options="options" :disabled="loading"/>
      </div>
    </v-row>

    <!-- Filter Section (Master Pattern) -->
    <v-card outlined class="modern-card pa-6 mb-8 shadow-sm">
      <v-row dense align="end">
        <v-col cols="12" md="3">
          <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-1 ml-1">Desde</div>
          <v-text-field 
            v-model='filter.created_after' 
            outlined dense hide-details="auto"
            type="date" 
            :disabled="loading" 
            clearable 
            class="modern-input"
            @change="triggerFetch"
          />
        </v-col>
        <v-col cols="12" md="3">
          <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-1 ml-1">Hasta</div>
          <v-text-field 
            v-model='filter.created_before' 
            outlined dense hide-details="auto"
            type="date" 
            :disabled="loading" 
            clearable 
            class="modern-input"
            @change="triggerFetch"
          />
        </v-col>
        <v-col cols="12" md="3">
          <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-1 ml-1">Area de Producción</div>
          <v-select
            v-model="filter.machine"
            :items="machines"
            :loading="machinesLoading"
            item-text="name"
            item-value="id"
            return-object
            placeholder="Todas"
            outlined dense hide-details="auto"
            clearable
            class="modern-select"
            @change="triggerFetchMachine"
          />
        </v-col>
        <v-col cols="12" md="3">
          <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-1 ml-1">Equipo</div>
          <v-select
            v-model="filter.equipment"
            :items="equipments"
            item-text="name"
            item-value="id"
            placeholder="Todos"
            outlined dense hide-details="auto"
            clearable
            :disabled="!filter.machine"
            class="modern-select"
            @change="triggerFetch"
          />
        </v-col>
      </v-row>
    </v-card>

    <!-- Main Results Section -->
    <v-card outlined class="modern-card overflow-hidden shadow-sm">
      <v-data-table
        :headers="headers"
        :items="items"
        :options.sync="options"
        :server-items-length="totalItems"
        :loading="loading"
        class="modern-table"
        @update:options="fetch"
        :footer-props="{
          showFirstLastPage: true,
          itemsPerPageOptions: [10, 20, 30, 40, 50, 100],
        }"
      >
        <!-- Search Bar in Top Slot (Master Pattern) -->
        <template v-slot:top>
          <div class="px-6 py-4 border-bottom d-flex align-center bg-white">
            <v-text-field
              v-model="search"
              placeholder="Buscar por OT, Producto, Cliente u OT..."
              prepend-inner-icon="fas fa-search"
              outlined dense hide-details="auto"
              clearable
              :disabled="loading"
              class="modern-search-field flex-grow-1"
              @keyup="searchTimeOut"
            />
          </div>
        </template>
        
        <template v-slot:item.created_at="{ item }">
          <div class="text-caption font-weight-bold grey--text">{{item.created_at | moment("DD/MM/YYYY")}}</div>
        </template>
        
        <template v-slot:item.process_order__product__work_order__id="{item}">
          <show-work-order :work_order_id="item.work_order" class="text-decoration-none">
            <div class="caption grey--text mb-n1">{{item.customer_name}}</div>
            <div class="primary--text font-weight-black">OT {{item.work_order}}</div>
          </show-work-order>
        </template>
        
        <template v-slot:item.process_order__product__name="{item}">
          <div class="caption grey--text uppercase tracking-tighter mb-n1">[{{item.product_sku}}]</div>
          <div class="caption font-weight-bold text-truncate" style="max-width: 250px">{{item.product_name}}</div>
        </template>
        
        <template v-slot:item.product_qty="{ item }">
          <div class="text-end">
            <div class="font-weight-black">{{item.product_qty | number('0,0[.]00')}} <v-icon x-small class="ml-1">view_comfy</v-icon></div>
            <div class="caption grey--text">{{item.product_mts2 | number('0,0.00')}} m²</div>
            <v-icon class="error--text ml-2" x-small v-if="item.reprocess">fas fa-clock-rotate-left</v-icon>
          </div>
        </template>
        
        <template v-slot:item.process_order__name="{ item }">
           {{item.process_order_name}}
        </template>
        
        <template v-slot:item.process_order__machine__name="{ item }">
          <v-chip x-small outlined label color="primary" class="font-weight-black">{{item.machine_name}}</v-chip>
        </template>
        
        <template v-slot:item.equipment__name="{ item }">
          <div class="caption grey--text font-weight-bold">{{item.equipment_name || 'N/A'}}</div>
        </template>
        
        <template v-slot:item.production="{ item }">
          <v-tooltip bottom content-class="premium-tooltip">
            <template v-slot:activator="{ on, attrs }">
              <div v-bind="attrs" v-on="on" class="text-end">
                <div class="font-weight-black secondary--text">
                  {{(item.machine_production_unit_m2?item.mts2:item.qty)|number('0,0.00')}} 
                  <v-icon small class="ml-1">{{item.machine_production_unit_m2?'square_foot':'view_comfy'}}</v-icon>
                </div>
                <div v-if="item.duration" class="caption grey--text">{{item.duration | number('0,0[.]0') }} <v-icon x-small class="ml-1">far fa-clock</v-icon> min</div>
              </div>              
            </template>
            <div class="pa-2">
              <div class="caption white--text opacity-70 mb-1">DETALLE DE TIEMPOS</div>
              <div class="d-flex align-center mb-1">
                <v-icon x-small color="success" class="mr-2">fas fa-play</v-icon>
                <span class="caption white--text"><b>Inicio:</b> {{item.start | moment("DD/MM/YYYY HH:mm")}}</span>
              </div>
              <div class="d-flex align-center">
                <v-icon x-small color="error" class="mr-2">fas fa-stop</v-icon>
                <span class="caption white--text"><b>Término:</b> {{item.end | moment("DD/MM/YYYY HH:mm")}}</span>
              </div>
            </div>
          </v-tooltip>
        </template>
        
        <template v-slot:item.created_by__first_name="{ item }">
          <div class="d-flex align-center justify-end">
            <v-tooltip left content-class="premium-tooltip">
              <template v-slot:activator="{ on, attrs }">
                <div v-bind="attrs" v-on="on" class="caption font-weight-bold grey--text text-end mr-3">
                  {{item.created_by}}
                </div>
              </template>
              <div class="pa-1 caption white--text">{{item.notes ? item.notes : 'Sin Observaciones'}}</div>
            </v-tooltip>
            
            <v-menu left bottom offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon v-bind="attrs" v-on="on" small>
                  <v-icon x-small>fas fa-ellipsis-v</v-icon>
                </v-btn>
              </template>
              <v-list dense class="modern-list pa-0">
                <v-list-item v-if="$can('delete','ProductionBitacora')" @click="confirmDelete(item)" class="item-error">
                  <v-list-item-icon class="mr-2"><v-icon x-small color="error">delete</v-icon></v-list-item-icon>
                  <v-list-item-title class="error--text caption font-weight-bold">Eliminar</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <confirm
      v-if="itemToDelete"
      v-model="showConfirmDelete" 
      :message="`¿Confirma eliminar el registro de OT ${itemToDelete.work_order}? Esta operación no se puede deshacer.`"
      @ok="deleteItem(itemToDelete)"
      @cancel="()=>{showConfirmDelete=false;itemToDelete=''}"/>
  </v-container>
</template>

<script>
import {fetchBitacora,fetchMachines, deleteBitacora} from '@/api/production'
import ExportProductionBitacora from '@/components/production/ExportProductionBitacora'
import notifications from '@/utils/notifications'
import moment from 'moment'
export default {
  name:'ProductionBitacora',
  components:{ExportProductionBitacora},
  data() {
    return {
      loading:false,
      filter:{
        created_after:moment(new Date(new Date().getFullYear(), new Date().getMonth(), 1)).format('yyyy-MM-DD'),
        created_before:'',
        machine:'',
        equipment:'',
        },
      options:{},
      totalItems:0,
      display:false,
      search:'',
      items:[],
      machines:[],
      itemToDelete:'',
      showConfirmDelete:false,
      machinesLoading:false,
      headers:[
          {text: 'Creada',align: 'start',value: 'created_at',class: 'table-header2',divider:true},
          {text: 'Cliente | OT',align: 'start',value: 'process_order__product__work_order__id',class: 'table-header2',divider:true,},
          {text: 'Producto',align: 'start',value: 'process_order__product__name',class: 'table-header2'},
          {text: 'Cant. Mts2',align: 'end',value: 'product_qty',class: 'table-header2',sortable:false,divider:true,width:'12%'},
          {text: 'Proceso',align: 'start',value: 'process_order__name',class: 'table-header2',width:'15%'},
          {text: 'Area Prod.',align: 'start',value: 'process_order__machine__name',class: 'table-header2',sortable:true,align:'start',width:'12%'},
          {text: 'Equip.',align: 'start',value: 'equipment__name',class: 'table-header2',sortable:true,align:'start',width:'12%'},
          {text: 'Producido',align: 'end',value: 'production',class: 'table-header2',sortable:false},
          {text:'Usuario',align:'end',value:'created_by__first_name',class:'table-header2',sortable:true},
        ]
    }
  },
  methods:{
    triggerFetch() {
      this.options.page = 1
      this.fetch()
    },
    triggerFetchMachine() {
      this.filter.equipment = null
      this.triggerFetch()
    },
    async fetch(){
      this.loading=true
      if(!this.filter.machine) this.filter.machine={id:''}
      if(!this.filter.equipment) this.filter.equipment=''
      var created_before = ''
      var created_after = ''
      if(this.filter.created_after) created_after = moment(this.filter.created_after).utc().format('yyyy-MM-DD HH:mm')
      if(this.filter.created_before) created_before=moment(this.filter.created_before).utc().add(1439,'minutes').format('yyyy-MM-DD HH:mm')
      const {sortBy, sortDesc,page, itemsPerPage }=this.options
      const query=`ordering=${sortDesc[0]?sortBy[0]:'-'+sortBy[0]}&page=${page}&page_size=${itemsPerPage}&search=${this.search}&created_after=${created_after}&created_before=${created_before}&process_order__machine__id=${this.filter.machine.id}&equipment__id=${this.filter.equipment}`
      await fetchBitacora(query)
      .then((data)=>{
        this.totalItems=data.count
        this.items = data.results
      })
      .catch((err)=>notifications.showError('Error Cargando Ordenes de Trabajo' + err))
      this.loading=false
    },
    searchTimeOut() {  
      if (this.timer) {
          clearTimeout(this.timer);
          this.timer = null;
      }
      this.timer = setTimeout(() => {
          this.fetch()
      }, 800);
    },
    confirmDelete(item){
      this.itemToDelete=item
      this.showConfirmDelete=true
    },
    async deleteItem(item){
      this.loading=true
      this.showConfirmDelete=false
      await deleteBitacora(item.id)
      .then(()=>{
        notifications.showSuccess('Registro Eliminado')
      })
      .catch((err)=>notifications.showError(err))
      .finally(()=>{
        this.loading=false
        this.fetch()
      })
    }
  },
  async mounted(){
    await this.fetch()
    this.machinesLoading=true
    this.machines=await fetchMachines('enabled=true&ordering=sort')
    this.machinesLoading=false
  },
  computed:{
      equipments(){
        return this.filter.machine?this.filter.machine.equipments:[]
      }
    }
}
</script>

<style scoped>
.modern-bitacora-view { background-color: #f4f7f9; min-height: 100vh; }
.modern-card { border-radius: 12px !important; border: 1px solid rgba(0,0,0,0.05) !important; background-color: white !important; }
.shadow-sm { box-shadow: 0 2px 12px rgba(0,0,0,0.04) !important; }
.border-white-sm { border: 2px solid white !important; }
.modern-input >>> .v-input__control fieldset { border-radius: 8px !important; border-color: #e0e4e8 !important; }
.modern-select >>> .v-input__control fieldset { border-radius: 8px !important; border-color: #e0e4e8 !important; }
.modern-search-field >>> .v-input__control fieldset { border-radius: 12px !important; border-color: #e0e4e8 !important; background-color: #f8fbfc !important; }
.modern-search-field >>> .v-input__prepend-inner { margin-top: 2px !important; margin-right: 8px !important; }
.border-bottom { border-bottom: 1px solid rgba(0,0,0,0.05) !important; }
.lh-1 { line-height: 1 !important; }
.uppercase { text-transform: uppercase; }
.tracking-widest { letter-spacing: 2px; }
.tracking-tighter { letter-spacing: -0.5px; }
.gap-3 { gap: 12px; }
.opacity-70 { opacity: 0.7; }

.modern-table >>> .v-data-table-header th { white-space: nowrap; height: 56px !important; text-transform: uppercase; font-size: 0.7rem !important; letter-spacing: 1.5px; font-weight: 900 !important; background-color: #f8fbfc !important; color: #607d8b !important; }
.modern-table >>> td { padding: 12px 16px !important; border-bottom: 1px solid #f0f3f5 !important; }
.modern-table >>> tr:hover { background-color: rgba(var(--v-primary-base), 0.02) !important; }

.modern-list .v-list-item:hover { background-color: rgba(var(--v-primary-base), 0.05); }
.item-error:hover { background-color: rgba(var(--v-error-base), 0.05) !important; }

.premium-tooltip { background: rgba(33, 33, 33, 0.95) !important; backdrop-filter: blur(4px); border-radius: 12px !important; border: 1px solid rgba(255,255,255,0.1); }
</style>
